import { initializeApp } from "firebase/app";
import { getStorage } from "firebase/storage";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { getApiBaseUrl, joinUrl } from "./queryClient";

// Get Firebase config from environment variables
const apiKey = process.env.TOKENS_LAUNCHPAD_VITE_FIREBASE_API_KEY || '';
const projectId = process.env.APPCONSTANTS_LAUNCHPAD_LAUNCHPADVITE_FIREBASE_PROJECT_ID || '';
const storageBucket = process.env.APPCONSTANTS_LAUNCHPAD_LAUNCHPADVITE_FIREBASE_BUCKET || `${projectId}.firebasestorage.app`;
const authDomain = process.env.APPCONSTANTS_LAUNCHPAD_LAUNCHPADVITE_FIREBASE_AUTHDOMAIN || `${projectId}.firebaseapp.com`;
// Validate required Firebase configuration
if (!apiKey || apiKey === 'your-firebase-api-key' || apiKey.trim() === '') {
  console.error('❌ Firebase API key is missing or invalid!');
  console.error('   Please set TOKENS_LAUNCHPAD_VITE_FIREBASE_API_KEY in your .env file');
  throw new Error('Firebase API key is not configured. Please set TOKENS_LAUNCHPAD_VITE_FIREBASE_API_KEY in your environment variables.');
}

if (!projectId || projectId === 'your-firebase-project-id' || projectId.trim() === '') {
  console.error('❌ Firebase Project ID is missing or invalid!');
  console.error('   Please set APPCONSTANTS_LAUNCHPAD_LAUNCHPADVITE_FIREBASE_PROJECT_ID in your .env file');
  throw new Error('Firebase Project ID is not configured. Please set APPCONSTANTS_LAUNCHPAD_LAUNCHPADVITE_FIREBASE_PROJECT_ID in your environment variables.');
}

const firebaseConfig = {
  apiKey: apiKey,
  authDomain: authDomain, // Authdomain (different from storage bucket)
  projectId: projectId,
  storageBucket: storageBucket, // Google Cloud Storage bucket for file storage
};

// Log configuration status (without sensitive data)
console.log('🔥 Firebase Client SDK Configuration:');
console.log(`   Project ID: ${projectId}`);
console.log(`   API Key: ${apiKey.substring(0, 10)}...${apiKey.substring(apiKey.length - 4)} (${apiKey.length} chars)`);
console.log(`   Auth Domain: ${firebaseConfig.authDomain}`);
console.log(`   Storage Bucket: ${firebaseConfig.storageBucket}`);

// Initialize Firebase
let app;
try {
  app = initializeApp(firebaseConfig);
  console.log('✅ Firebase Client SDK initialized successfully');
} catch (error: any) {
  console.error('❌ Failed to initialize Firebase Client SDK:', error.message);
  if (error.message.includes('API key')) {
    throw new Error(
      'Invalid Firebase API key. Please check:\n' +
      '1. TOKENS_LAUNCHPAD_VITE_FIREBASE_API_KEY is set correctly in your .env file\n' +
      '2. The API key is valid in Firebase Console → Project Settings → General\n' +
      '3. API key restrictions allow your domain (if restrictions are enabled)'
    );
  }
  throw error;
}

// Initialize Firebase Auth and Storage
export const auth = getAuth(app);
export const storage = getStorage(app);

// Authenticate user to Firebase using custom token from backend
export const ensureAuthenticated = async () => {
  try {
    const baseUrl = getApiBaseUrl();
    // First check server session
    const meUrl = baseUrl ? joinUrl(baseUrl, '/auth/me') : '/auth/me';
    const response = await fetch(meUrl, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      console.log('User not authenticated, redirecting to login...');
      const baseUrl = getApiBaseUrl();
      const googleAuthUrl = baseUrl ? joinUrl(baseUrl, '/auth/google') : '/auth/google';
      window.location.href = googleAuthUrl;
      throw new Error('Authentication required');
    }

    const data = await response.json();
    console.log('User authenticated via server session:', data.user.email);

    // Check if already authenticated to Firebase
    if (auth.currentUser && auth.currentUser.email === data.user.email) {
      console.log('Already authenticated to Firebase:', auth.currentUser.email);
      return data.user;
    }

    // Get custom Firebase token from backend
    console.log('Getting custom Firebase token from backend...');
    const tokenUrl = baseUrl ? joinUrl(baseUrl, '/auth/firebase-token') : '/auth/firebase-token';
    const tokenResponse = await fetch(tokenUrl, {
      credentials: 'include'
    });

    if (!tokenResponse.ok) {
      let errorMessage = 'Failed to get Firebase token';
      try {
        const errorData = await tokenResponse.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
        console.error('Failed to get Firebase token:', {
          status: tokenResponse.status,
          error: errorData.error,
          message: errorData.message,
          code: errorData.code
        });
      } catch (parseError) {
        const errorText = await tokenResponse.text();
        console.error('Failed to get Firebase token (non-JSON response):', errorText);
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const { token } = await tokenResponse.json();
    console.log('Received custom token, authenticating to Firebase...');

    // Sign in to Firebase with custom token
    try {
      const userCredential = await signInWithCustomToken(auth, token);
      console.log('✅ Successfully authenticated to Firebase:', userCredential.user.email);
    } catch (signInError: any) {
      console.error('❌ Failed to sign in with custom token:', signInError);
      console.error('   Error code:', signInError.code);
      console.error('   Error message:', signInError.message);
      console.error('   Client Firebase config:', {
        projectId: projectId,
        apiKey: apiKey ? `${apiKey.substring(0, 10)}...${apiKey.substring(apiKey.length - 4)}` : 'NOT SET',
        authDomain: firebaseConfig.authDomain,
        storageBucket: firebaseConfig.storageBucket,
      });
      
      // Check for configuration not found error (project ID mismatch)
      if (signInError.code === 'auth/configuration-not-found' || 
          signInError.message?.includes('no configuration corresponding to the provided identifier')) {
        throw new Error(
          `Firebase configuration mismatch detected!\n\n` +
          `This error typically means:\n` +
          `1. The server generated a custom token for a different Firebase project than the client is configured with\n` +
          `2. The API key doesn't match the project ID\n` +
          `3. The Firebase project doesn't exist or is misconfigured\n\n` +
          `Current client configuration:\n` +
          `  - Project ID: ${projectId}\n` +
          `  - API Key: ${apiKey ? apiKey.substring(0, 10) + '...' + apiKey.substring(apiKey.length - 4) : 'NOT SET'}\n` +
          `  - Auth Domain: ${firebaseConfig.authDomain}\n\n` +
          `Troubleshooting steps:\n` +
          `1. Verify that APPCONSTANTS_LAUNCHPAD_LAUNCHPADVITE_FIREBASE_PROJECT_ID matches on both client and server\n` +
          `2. Verify that TOKENS_LAUNCHPAD_VITE_FIREBASE_API_KEY matches the project ID\n` +
          `3. Check that the server's FIREBASE_SERVICE_ACCOUNT project_id matches the client's project ID\n` +
          `4. Verify the API key is correct in Firebase Console → Project Settings → General\n` +
          `5. Ensure both client and server are using the same Firebase project`
        );
      }
      
      // Check for API key errors
      if (signInError.code === 'auth/api-key-not-valid' || 
          signInError.message?.includes('API key') ||
          signInError.message?.includes('INVALID_ARGUMENT') ||
          signInError.code?.includes('INVALID_ARGUMENT')) {
        throw new Error(
          'Firebase API key is invalid. Please check:\n' +
          '1. TOKENS_LAUNCHPAD_VITE_FIREBASE_API_KEY is set correctly in your .env file\n' +
          '2. The API key matches your Firebase project\n' +
          '3. API key restrictions allow your domain (if enabled)\n' +
          '4. Get the correct API key from Firebase Console → Project Settings → General → Your apps\n' +
          `Current API key: ${apiKey.substring(0, 10)}...${apiKey.substring(apiKey.length - 4)}`
        );
      }
      throw signInError;
    }

    return data.user;
  } catch (error: any) {
    console.error('Authentication check failed:', error);
    
    // Provide more helpful error messages for common issues
    if (error.message?.includes('Firebase project configuration not found') ||
        error.message?.includes('configuration-not-found') ||
        error.message?.includes('project ID doesn\'t exist')) {
      const enhancedError = new Error(
        'Firebase authentication failed: The Firebase project is not properly configured on the server. ' +
        'Please contact your administrator. This is a server-side configuration issue, not a problem with your login.'
      );
      (enhancedError as any).code = 'FIREBASE_CONFIG_ERROR';
      throw enhancedError;
    }
    
    throw error;
  }
};

export default app;
